import React, { useEffect } from 'react';
import { useI18n } from '../i18n/i18nContext';
import { useServiceStore } from '../stores/serviceStore';
import LocalizedLink from './LocalizedLink';
import ContentPageRenderer from './ContentPageRenderer';

const Footer: React.FC = () => {
  const { language, t } = useI18n();
  const { services, fetchServices } = useServiceStore();
  const mainSiteUrl = 'https://www.fireandice.ee';

  // Add language prefix for Estonian
  const getExternalUrl = (path: string) => {
    return language === 'et' ? `${mainSiteUrl}/et${path}` : `${mainSiteUrl}${path}`;
  };

  // Fetch services when component mounts
  useEffect(() => {
    if (services.length === 0) {
      fetchServices();
    }
  }, [fetchServices, services.length]);

  const mainLinks = [
    { name: t.footer.specialOffers, href: '/promotions', isLocal: true },
    { name: t.footer.pricing, href: '/pricing', isLocal: true },
  ];

  return (
    <footer className="bg-gray-800 text-white">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Logo and Contact Info */}
          <div className="mb-6 md:mb-0">
            <a href={getExternalUrl('')} target="_blank" rel="noopener noreferrer">
              <img src="/images/logo-white.svg" alt="Fire and Ice" className="h-10" />
            </a>
            
            <div className="mt-6">
              <ContentPageRenderer 
                slug="footer-contact-info" 
                className="prose-invert prose-sm text-gray-300"
              />
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t.footer.services}</h3>
            <ul className="space-y-2">
              {services.map((service) => {
                const serviceName = service[`name_${language}` as keyof typeof service] as string || service.name;
                return (
                  <li key={service.id}>
                    <LocalizedLink
                      to={`/services/${service.slug}`}
                      className="hover:underline text-gray-300 text-sm"
                    >
                      {serviceName}
                    </LocalizedLink>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Other Links */}
          <div>
            <ul className="space-y-4">
              <li>
                <LocalizedLink to="/gallery" className="font-bold hover:underline">
                  {t.footer.gallery}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/p/science-studies" className="font-bold hover:underline">
                  {t.footer.scienceAndStudies}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/contact" className="font-bold hover:underline uppercase">
                  {t.nav.about}
                </LocalizedLink>
              </li>
              {mainLinks.map((link) => (
                <li key={link.name}>
                  {link.isLocal ? (
                    <LocalizedLink
                      to={link.href}
                      className="font-bold hover:underline"
                    >
                      {link.name}
                    </LocalizedLink>
                  ) : (
                    <a
                      href={getExternalUrl(link.href)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold hover:underline"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
              <li>
                <LocalizedLink to="/book" className="font-bold hover:underline">
                  {t.footer.bookNow}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/check-balance" className="font-bold hover:underline">
                  {t.footer.checkGiftCardBalance || 'Check Gift Card Balance'}
                </LocalizedLink>
              </li>
            </ul>
          </div>

        </div>
        
        <div className="mt-8 border-t border-gray-700 pt-6 pb-8 md:pb-4 text-center text-gray-400 text-sm">
          <p className="mb-3 md:mb-0">
            {t.footer.copyright.replace('{year}', new Date().getFullYear().toString())}
            <span className="hidden md:inline">
              <LocalizedLink to="/p/privacy" className="ml-4 text-gray-400 hover:text-gray-300 underline">
                {t.footer.privacyNotice}
              </LocalizedLink>
              <LocalizedLink to="/p/terms" className="ml-4 text-gray-400 hover:text-gray-300 underline">
                {t.footer.termsOfService}
              </LocalizedLink>
            </span>
          </p>
          <p className="md:hidden">
            <LocalizedLink to="/p/privacy" className="mr-4 text-gray-400 hover:text-gray-300 underline">
              {t.footer.privacyNotice}
            </LocalizedLink>
            <LocalizedLink to="/p/terms" className="text-gray-400 hover:text-gray-300 underline">
              {t.footer.termsOfService}
            </LocalizedLink>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;